import { CartApi } from '../../api/cart/cartApi';
import {
  AddItem,
  Cart,
  CustomerPlacement,
  isAddItem,
  isUpdateItem,
  ItemOptionType,
  PreSale,
  RemoveItem,
  UpdateCart,
  UpdateItem,
} from '../../api';
import { buildItemIdentifier, calculateSubTotal, groupBundleItems } from '../../functions';
import {
  mapDeliveryExpectation,
  mapDeliveryMethod,
} from '../../functions/deliveryExpectation/deliveryExpectation';
import { camelize, decamelize } from 'humps';

export class CartService {
  private readonly cartApi = new CartApi();

  public async get(id?: number): Promise<Cart> {
    return id ? await this.cartApi.getCart(id) : await this.cartApi.create();
  }

  public async update(id: number, cart: UpdateCart): Promise<Cart> {
    const cartItems = cart.cartItems?.map((item) => {
      if (isAddItem(item)) return this.buildAddCartItem(item);
      if (isUpdateItem(item)) return this.buildUpdateItem(item);

      return this.buildRemoveItem(item);
    });

    return await this.cartApi.update(id, { ...cart, cartItems });
  }

  public mapCart(cart: Cart) {
    cart.subtotal = calculateSubTotal(cart.cartItems);

    cart.cartItems.forEach((item) => {
      item.deliveryMethod = mapDeliveryMethod(item);
      item.customerPlacement = camelize(item.customerPlacement);
      item.retailerName = item.retailerName || item.supplierName;
      const preSale =
        item.customerPlacement === 'preSale'
          ? ({
              active: true,
              purchasable: true,
              expectedDeliveryDate: item.product?.preSaleExpectation as Date,
            } as PreSale)
          : undefined;
      const expectation = mapDeliveryExpectation({
        ...item,
        preSale,
      });
      item.deliveryExpectation = expectation.deliveryExpectation;
      item.shortDeliveryExpectation = expectation.shortDeliveryExpectation;
      item.name = item.productGrouping.name;
      item.image = item.product.imageUrl;
      item.price = item.product.price;
      item.inStock = item.product.inStock;
      item.size = item.product.volume;
      item.type = item.product.containerType;

      if (item.supplierName) delete item.supplierName;
    });

    cart.cartItems = groupBundleItems(cart.cartItems);
    cart.itemCount = cart.cartItems.reduce((sum, item) => sum + item.quantity, 0);

    return cart;
  }

  private buildAddCartItem(item: AddItem) {
    const { variantId, options, bundleExternalId, customerPlacement, quantity } = item;
    if (options && (options.line1 || options.line2 || options.line3 || options.line4)) {
      options.type = ItemOptionType.Engraving;
    }
    if (options && options.sender) {
      options.type = ItemOptionType.GiftCard;
    }

    return {
      ...item,
      identifier: buildItemIdentifier(variantId, options, bundleExternalId),
      productBundleExternalId: bundleExternalId,
      customerPlacement: decamelize(customerPlacement || 'standard') as CustomerPlacement,
      quantity: quantity ?? 1,
      options: options?.type ? options : undefined,
    };
  }

  private buildUpdateItem(item: UpdateItem) {
    if (!item.options) delete item.options;

    return {
      ...item,
      productBundleExternalId: item.bundleExternalId,
      customerPlacement: decamelize(item.customerPlacement) as CustomerPlacement,
    };
  }

  private buildRemoveItem(item: RemoveItem) {
    return {
      quantity: item.quantity,
      identifier: item.identifier,
    };
  }
}
